<template>
  <div class="extras-mg-grafica">
    <div class="widget__header widget__header--no-border">
      <div class="widget__header-main">
        <div class="widget__header-title-wrapper">
          <div class="widget__header-title">{{ widget.title }}</div>
          <Badge v-if="sinConfigurar" color="red" indicator rounded>
            Falta configurar
          </Badge>
        </div>
        <div v-if="widget.description" class="widget__header-description">
          {{ widget.description }}
        </div>
      </div>
      <WidgetContextMenu
        v-if="isEditMode"
        :widget="widget"
        :dashboard="dashboard"
        @delete-widget="$emit('delete-widget', $event)"
      ></WidgetContextMenu>
    </div>

    <div class="widget__content extras-mg-grafica__lienzo">
      <div v-if="loading" class="loading-spinner"></div>
      <div v-else-if="sinConfigurar" class="extras-mg-grafica__aviso">
        Elige la tabla y el campo por el que quieres agrupar.
      </div>
      <div v-else-if="!datos.labels.length" class="extras-mg-grafica__aviso">
        No hay filas que graficar.
      </div>
      <template v-else>
        <div v-if="hayMasFilas" class="extras-mg-grafica__tope">
          Cubre solo las primeras 200 filas: la tabla tiene mas.
        </div>
        <component :is="componenteGrafica" :data="datos" :options="opciones" />
      </template>
    </div>
  </div>
</template>

<script>
import { Chart as ChartJS, registerables } from 'chart.js'
import { Bar, Line, Pie, Doughnut } from 'vue-chartjs'

import WidgetContextMenu from '@baserow/modules/dashboard/components/widget/WidgetContextMenu'
import tableFields from '@baserow/modules/database/mixins/tableFields'

// chart.js 3 no registra nada por defecto; sin esto el lienzo sale en blanco.
ChartJS.register(...registerables)

// Los mismos colores con los que se pintan las etiquetas en la piel, para que
// una grafica agrupada por un campo de seleccion se lea junto a su tabla. Si
// algun dia se apaga la piel, siguen siendo colores legibles sobre blanco: no
// son nada mas que una paleta.
const PALETA = [
  '#00c875', '#fdab3d', '#e2445c', '#0086c0', '#a25ddc',
  '#ffcb00', '#66ccff', '#ff5ac4', '#c4c4c4', '#037f4c',
]

export default {
  name: 'GraficaWidget',
  components: { WidgetContextMenu, Bar, Line, Pie, Doughnut },
  mixins: [tableFields],
  props: {
    dashboard: { type: Object, required: true },
    widget: { type: Object, required: true },
    storePrefix: { type: String, required: false, default: '' },
    loading: { type: Boolean, required: false, default: false },
  },
  emits: ['delete-widget'],
  computed: {
    isEditMode() {
      return this.$store.getters[
        `${this.storePrefix}dashboardApplication/isEditMode`
      ]
    },
    dataSource() {
      return this.$store.getters[
        `${this.storePrefix}dashboardApplication/getDataSourceById`
      ](this.widget.data_source_id)
    },
    bloqueDeDatos() {
      return this.$store.getters[
        `${this.storePrefix}dashboardApplication/getDataForDataSource`
      ](this.dataSource?.id)
    },
    datosCrudos() {
      const d = this.bloqueDeDatos
      if (!d) {
        return []
      }
      // El servicio de listar filas entrega {results, has_next_page}.
      if (Array.isArray(d.results)) {
        return d.results
      }
      return Array.isArray(d.result) ? d.result : d.result?.results || []
    },
    /**
     * El servicio pagina y su tope duro son 200 filas
     * (BASEROW_INTEGRATION_LOCAL_BASEROW_PAGE_SIZE_LIMIT). Si la tabla tiene
     * mas, la grafica cubre solo las primeras: hay que decirlo, no callarlo.
     */
    hayMasFilas() {
      return !!this.bloqueDeDatos?.has_next_page
    },
    sinConfigurar() {
      return !this.dataSource?.table_id || !this.widget.campo_grupo
    },
    componenteGrafica() {
      return (
        { bar: 'Bar', line: 'Line', pie: 'Pie', doughnut: 'Doughnut' }[
          this.widget.tipo_grafica
        ] || 'Bar'
      )
    },
    datos() {
      const grupos = this.agrupar()
      const etiquetas = Object.keys(grupos)
      return {
        labels: etiquetas,
        datasets: [
          {
            label: this.widget.title || 'Total',
            data: etiquetas.map((e) => grupos[e]),
            backgroundColor: etiquetas.map(
              (_, i) => PALETA[i % PALETA.length]
            ),
            borderColor: this.esCircular ? '#fff' : PALETA[0],
            borderWidth: this.esCircular ? 2 : 1,
          },
        ],
      }
    },
    esCircular() {
      return ['pie', 'doughnut'].includes(this.widget.tipo_grafica)
    },
    opciones() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: this.esCircular } },
      }
    },
  },
  methods: {
    /* Lo pide el mixin tableFields. */
    getTableId() {
      return this.dataSource?.table_id
    },
    /**
     * Agrupa y agrega en el navegador. Es a proposito: el servicio de
     * agregacion agrupada es de la edicion de pago, y para tablas de cientos o
     * pocos miles de filas hacerlo aqui no se nota.
     */
    agrupar() {
      // El servicio devuelve las filas con el NOMBRE de la columna como llave,
      // no con field_<id>. Se traduce el id guardado usando los campos de la
      // tabla, para que renombrar una columna no rompa la grafica.
      const llaveGrupo = this.nombreDeCampo(this.widget.campo_grupo)
      const llaveValor = this.nombreDeCampo(this.widget.campo_valor)
      if (!llaveGrupo) {
        return {}
      }
      const modo = this.widget.agregacion || 'count'

      const acumulado = {}
      for (const fila of this.datosCrudos) {
        const etiqueta = this.aTexto(fila[llaveGrupo])
        const bucket = (acumulado[etiqueta] = acumulado[etiqueta] || [])
        if (modo === 'count') {
          bucket.push(1)
        } else if (llaveValor) {
          const n = parseFloat(fila[llaveValor])
          if (!isNaN(n)) {
            bucket.push(n)
          }
        }
      }

      const resultado = {}
      for (const [etiqueta, valores] of Object.entries(acumulado)) {
        resultado[etiqueta] = this.agregar(modo, valores)
      }
      return resultado
    },
    nombreDeCampo(id) {
      if (!id) {
        return null
      }
      const campo = this.tableFields.find((f) => f.id === id)
      return campo ? campo.name : null
    },
    agregar(modo, valores) {
      if (!valores.length) {
        return 0
      }
      const suma = valores.reduce((a, b) => a + b, 0)
      switch (modo) {
        case 'count':
          return valores.length
        case 'sum':
          return suma
        case 'avg':
          return Math.round((suma / valores.length) * 100) / 100
        case 'min':
          return Math.min(...valores)
        case 'max':
          return Math.max(...valores)
        default:
          return valores.length
      }
    },
    /**
     * Un campo puede traer texto, una opcion {value,color}, una lista de
     * opciones o null. Todo eso tiene que caber en una etiqueta del eje.
     */
    aTexto(valor) {
      if (valor === null || valor === undefined || valor === '') {
        return '(vacio)'
      }
      if (Array.isArray(valor)) {
        return valor.map((v) => this.aTexto(v)).join(', ') || '(vacio)'
      }
      if (typeof valor === 'object') {
        return String(valor.value ?? valor.name ?? valor.id ?? '(vacio)')
      }
      return String(valor)
    },
  },
}
</script>

<style lang="scss">
.extras-mg-grafica__lienzo {
  position: relative;
  min-height: 180px;
  height: 100%;
  padding: 8px 12px 12px;
}

.extras-mg-grafica__tope {
  font-size: 11px;
  color: #b45309;
  background: #fffbeb;
  border-radius: 4px;
  padding: 4px 8px;
  margin-bottom: 6px;
}

.extras-mg-grafica__aviso {
  font-size: 12px;
  opacity: 0.6;
  padding: 24px 0;
  text-align: center;
}
</style>
